/* eslint-disable react-refresh/only-export-components --
 * Deliberately a mixed module: the TMF fetch/grouping helpers and the tables
 * that render them are one unit shared by the trial Documents screen and the
 * site-profile Documents tab. Splitting them would separate the data shape
 * from its only renderers. */
import type { ReactNode } from "react";
import { Progress, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";

import { apiFetchJson } from "@/utils/apiFetch";
import InfoTooltip from "@/components/UI/InfoTooltip";

/** Shared TMF document machinery: the `documents` dataset fetch, the artifact-
 * section grouping, and the two reusable tables (needs-attention action queue
 * and the section checklist). Used by the trial Documents screen and the
 * site-profile Documents tab so both scopes tell the same story:
 * the action queue serves the site persona (what do I owe now); the checklist
 * serves the sponsor/CRA persona (is this workspace inspection-ready). */

export type DocRow = {
  document_id: string;
  title: string | null;
  document_type: string | null;
  category: string | null;
  binder: string | null;
  folder_path: string | null;
  workspace: string | null;
  mrm_status: string | null;
  open_reviews: number | null;
  form_status: string | null;
  file_name: string | null;
  ext: string | null;
  uploaded_date: string | null;
  due_date: string | null;
  expiration_date: string | null;
};

/**
 * What to call a row in a document list.
 *
 * Not `title`: in the Florence eBinders extract that column is anonymised to
 * generated business phrases ("Optimized value-added Graphic Interface") for
 * every row, so it reads as noise and says nothing about the trial. The head
 * version's `file_name` is the real document name and is present on every
 * collected document.
 *
 * A placeholder is an empty slot in the binder template — it has no document
 * and therefore no filename — so it is named by the folder that expects it
 * ("9.3. amendments - approvals and notifications"). Both come from the
 * source; nothing here is synthesised to fill a gap.
 */
export function docLabel(row: DocRow): string {
  if (row.file_name) return row.file_name;
  const leaf = (row.folder_path ?? "").split("/").filter(Boolean).pop();
  return leaf ?? "Untitled document";
}

export type Envelope = { count: number; results: DocRow[] };

export async function fetchTrialDocuments(trialId: string): Promise<Envelope> {
  return apiFetchJson<Envelope>(
    `${import.meta.env.VITE_API_URL}/data/trial/${encodeURIComponent(trialId)}` +
      `/filters/data/?dataset_type=documents&pagination=${encodeURIComponent(
        JSON.stringify({ page: 1, page_size: 2000 }),
      )}`,
  );
}

export type Stage = "activation" | "conduct" | "closeout";

/**
 * Trial-lifecycle stage of an artifact section. Startup sections (agreements,
 * qualifications, disclosures, IRB approval, protocol, lab certification)
 * form the activation package; per-subject and operational sections
 * accumulate during conduct; close-out artifacts arrive at the end. Mapped
 * here from the source template's section numbers — a production integration
 * would key off TMF Reference Model zone codes or source-system stage
 * metadata instead. Sections that span stages (IRB correspondence, lab
 * renewals) sit where their initial obligation is; renewals re-enter the
 * action queue via expiration dates.
 *
 * Informed consent (17 — ICF templates, HIPAA forms, assent) is a conduct
 * section, not an activation gate. The first approved template is a startup
 * prerequisite, but the section keeps accruing versions for the life of the
 * trial as protocol amendments drive re-approval and re-consent, so it can
 * never read "complete" — banding it with activation made routine amendment
 * activity look like an unmet startup obligation. Executed subject consents
 * live under subject files, which are conduct already.
 */
export function stageOf(order: number, isSubjectFiles: boolean): Stage {
  if (isSubjectFiles) return "conduct";
  if (order >= 23 && order < 9_000) return "closeout";
  if (order >= 1 && order <= 14) return "activation";
  return "conduct";
}

export type SectionRow = {
  key: string;
  section: string;
  order: number;
  stage: Stage;
  isSubjectFiles: boolean;
  expected: number;
  collected: number;
  openQueries: number;
  overdue: number;
  binders: Set<string>;
  docs: DocRow[];
};

/** Artifact section from the folder path (numbered taxonomy under the binder
 * root). Per-participant folders pool into "Subject files". */
export function classify(row: DocRow): {
  section: string;
  order: number;
  isSubjectFiles: boolean;
} {
  const parts = (row.folder_path ?? "").split("/").filter(Boolean);
  const raw = parts[1] ?? parts[0] ?? "Unfiled";
  if (/^subject\b/i.test(raw)) {
    return { section: "Subject files", order: 10_000, isSubjectFiles: true };
  }
  const m = raw.match(/^(\d+)[.)]/);
  return {
    section: raw,
    order: m ? parseInt(m[1], 10) : 9_000,
    isSubjectFiles: false,
  };
}

export function groupSections(rows: DocRow[], now: number): SectionRow[] {
  const byKey = new Map<string, SectionRow>();
  for (const r of rows) {
    const { section, order, isSubjectFiles } = classify(r);
    let s = byKey.get(section);
    if (!s) {
      s = {
        key: section,
        section,
        order,
        stage: stageOf(order, isSubjectFiles),
        isSubjectFiles,
        expected: 0,
        collected: 0,
        openQueries: 0,
        overdue: 0,
        binders: new Set(),
        docs: [],
      };
      byKey.set(section, s);
    }
    s.expected += 1;
    if (r.document_type === "content") s.collected += 1;
    s.openQueries += r.open_reviews ?? 0;
    if (
      r.document_type === "placeholder" &&
      r.due_date &&
      Date.parse(r.due_date) < now
    )
      s.overdue += 1;
    if (r.binder) s.binders.add(r.binder);
    s.docs.push(r);
  }
  return [...byKey.values()].sort(
    (a, b) => a.order - b.order || a.section.localeCompare(b.section),
  );
}

export const EXPIRY_WINDOW_DAYS = 90;

export type AttentionRow = DocRow & { reason: string };

/** The action queue: overdue expected documents, upcoming expirations, and
 * documents with open monitor queries — the site's to-do list. */
export function needsAttention(rows: DocRow[], now: number): AttentionRow[] {
  const horizon = now + EXPIRY_WINDOW_DAYS * 24 * 3600 * 1000;
  const overdue = rows.filter(
    (r) =>
      r.document_type === "placeholder" &&
      r.due_date &&
      Date.parse(r.due_date) < now,
  );
  const expiring = rows.filter(
    (r) =>
      r.expiration_date &&
      Date.parse(r.expiration_date) > now &&
      Date.parse(r.expiration_date) < horizon,
  );
  const queried = rows.filter((r) => (r.open_reviews ?? 0) > 0);
  return [
    ...overdue.map((r) => ({ ...r, reason: "Filing overdue" })),
    ...expiring.map((r) => ({ ...r, reason: "Expiring soon" })),
    ...queried.map((r) => ({ ...r, reason: "Open monitor query" })),
  ];
}

/** White-card shell for a row of metric tiles — the app-wide convention for
 * KPI strips (see MetricsShell on the site Overview tab). */
export function MetricsCard({
  columns,
  children,
}: {
  columns: number;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div
        className="grid divide-x divide-[#F0F2F4]"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {children}
      </div>
    </div>
  );
}

const attentionColumns: ColumnsType<AttentionRow> = [
  {
    title: "Action",
    dataIndex: "reason",
    width: 160,
    render: (v: string) => (
      <Tag color={v === "Open monitor query" ? "orange" : "red"}>{v}</Tag>
    ),
  },
  {
    title: "Document",
    key: "document",
    ellipsis: true,
    render: (_, r) => docLabel(r),
  },
  {
    title: "Section",
    key: "section",
    ellipsis: true,
    width: 220,
    render: (_, r) => classify(r).section,
  },
  { title: "MRM status", dataIndex: "mrm_status", width: 200, ellipsis: true },
];

export function NeedsAttentionTable({ rows }: { rows: AttentionRow[] }) {
  return (
    <Table
      size="small"
      rowKey={(r) => `${r.document_id}-${r.reason}`}
      columns={attentionColumns}
      dataSource={rows}
      scroll={{ x: 760 }}
      locale={{
        emptyText:
          "Nothing needs action — no overdue filings, expirations, or open queries.",
      }}
      pagination={{ pageSize: 8, hideOnSinglePage: true }}
    />
  );
}

const docColumns: ColumnsType<DocRow> = [
  {
    title: "Document",
    key: "document",
    ellipsis: true,
    render: (_, r) => docLabel(r),
  },
  {
    title: "Status",
    dataIndex: "document_type",
    width: 120,
    render: (v: string | null) =>
      v === "content" ? (
        <Tag color="green">collected</Tag>
      ) : (
        <Tag>expected</Tag>
      ),
  },
  { title: "Binder", dataIndex: "binder", ellipsis: true, width: 220 },
  { title: "MRM status", dataIndex: "mrm_status", width: 200, ellipsis: true },
  {
    title: "Queries",
    dataIndex: "open_reviews",
    width: 90,
    render: (v: number | null) => v ?? 0,
  },
];

const STAGE_META: {
  key: Stage;
  title: string;
  tooltip: string;
  emptyText: string;
}[] = [
  {
    key: "activation",
    title: "Site activation package",
    tooltip:
      "Essential documents required before the site opens — agreements, staff " +
      "qualifications, financial disclosures, IRB/EC approval, protocol, " +
      "laboratory certification, approved consent forms. Once collected they " +
      "remain on file for the life of the trial and stay fully visible here; " +
      "documents that expire (licenses, IRB continuing approvals) resurface " +
      "in the action queue as renewals come due. For an active site, any gap " +
      "in this package is a retrospective compliance finding.",
    emptyText: "No activation-stage sections in this workspace.",
  },
  {
    key: "conduct",
    title: "Trial conduct",
    tooltip:
      "Documents that accumulate while the trial runs — subject records, " +
      "case report forms, safety distribution, monitoring visit " +
      "documentation, and ongoing correspondence. This is where collection " +
      "focus sits for an active site; expected counts grow with enrollment " +
      "and monitoring activity.",
    emptyText: "No conduct-stage sections in this workspace.",
  },
  {
    key: "closeout",
    title: "Close-out",
    tooltip:
      "Artifacts added as the site approaches close-out — final monitoring " +
      "visit documentation, IRB/EC closure notification, final drug " +
      "accountability, archival confirmation. Nothing is expected in this " +
      "stage while the site is active.",
    emptyText: "No close-out artifacts expected yet — the site is active.",
  },
];

function stageChip(stage: Stage, expected: number, collected: number) {
  if (stage === "closeout" && expected === 0) return <Tag>Upcoming</Tag>;
  if (expected > 0 && collected === expected)
    return <Tag color="green">Complete — on file</Tag>;
  if (stage === "activation")
    return <Tag color="orange">{expected - collected} outstanding</Tag>;
  return <Tag color="blue">In progress</Tag>;
}

/** The section checklist split into trial-lifecycle bands, each with its own
 * completeness rollup. Completed stages stay fully expanded/discoverable —
 * records remain on file and visible for the life of the trial; only the
 * collection focus moves. */
export function StagedChecklist({
  sections,
  loading,
}: {
  sections: SectionRow[];
  loading?: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      {STAGE_META.map((meta) => {
        const rows = sections.filter((s) => s.stage === meta.key);
        const expected = rows.reduce((n, s) => n + s.expected, 0);
        const collected = rows.reduce((n, s) => n + s.collected, 0);
        const pct = expected ? Math.round((100 * collected) / expected) : 0;
        return (
          <div key={meta.key} className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[13px] font-semibold text-[#1F2933]">
                {meta.title}
              </span>
              <InfoTooltip title={meta.tooltip} />
              {stageChip(meta.key, expected, collected)}
              {expected > 0 && (
                <div className="flex items-center gap-2">
                  <Progress
                    percent={pct}
                    size="small"
                    style={{ width: 120, margin: 0 }}
                    status={collected === expected ? "success" : "normal"}
                  />
                  <span className="text-[12px] text-[#556C75] whitespace-nowrap">
                    {collected} / {expected}
                  </span>
                </div>
              )}
            </div>
            {rows.length > 0 ? (
              <SectionChecklistTable sections={rows} loading={loading} />
            ) : (
              <span className="text-[13px] text-[#556C75]">
                {meta.emptyText}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function SectionChecklistTable({
  sections,
  loading,
}: {
  sections: SectionRow[];
  loading?: boolean;
}) {
  const sectionColumns: ColumnsType<SectionRow> = [
    {
      title: "Artifact section",
      dataIndex: "section",
      ellipsis: true,
      render: (v: string, s) =>
        s.isSubjectFiles ? (
          <span className="font-medium">
            {v}{" "}
            <span className="text-[11px] text-[#8C8C8C]">
              (per-participant ISF records)
            </span>
          </span>
        ) : (
          <span className="font-medium">{v}</span>
        ),
    },
    {
      title: "Collected / Expected",
      key: "progress",
      width: 260,
      render: (_, s) => (
        <div className="flex items-center gap-2">
          <Progress
            percent={
              s.expected ? Math.round((100 * s.collected) / s.expected) : 0
            }
            size="small"
            style={{ width: 140, margin: 0 }}
            status={s.collected === s.expected ? "success" : "normal"}
          />
          <span className="text-[12px] text-[#556C75] whitespace-nowrap">
            {s.collected} / {s.expected}
          </span>
        </div>
      ),
      sorter: (a, b) =>
        (a.expected ? a.collected / a.expected : 0) -
        (b.expected ? b.collected / b.expected : 0),
    },
    {
      title: "Open queries",
      dataIndex: "openQueries",
      width: 120,
      sorter: (a, b) => a.openQueries - b.openQueries,
      render: (v: number) =>
        v > 0 ? <Tag color="orange">{v}</Tag> : <span>0</span>,
    },
    {
      title: "Overdue",
      dataIndex: "overdue",
      width: 100,
      sorter: (a, b) => a.overdue - b.overdue,
      render: (v: number) =>
        v > 0 ? <Tag color="red">{v}</Tag> : <span>0</span>,
    },
    {
      title: "Binders",
      key: "binders",
      width: 90,
      render: (_, s) => s.binders.size,
    },
  ];

  return (
    <Table
      size="small"
      rowKey="key"
      loading={loading}
      columns={sectionColumns}
      dataSource={sections}
      scroll={{ x: 900 }}
      expandable={{
        expandedRowRender: (s) => (
          <Table
            size="small"
            rowKey="document_id"
            columns={docColumns.map((c) =>
              c.title === "Binder"
                ? {
                    ...c,
                    filters: [...s.binders]
                      .sort()
                      .map((b) => ({ text: b, value: b })),
                    onFilter: (value, record: DocRow) =>
                      record.binder === value,
                  }
                : c,
            )}
            dataSource={s.docs}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
          />
        ),
      }}
      locale={{ emptyText: "No documents for this trial's workspace." }}
      pagination={false}
    />
  );
}
